import { Component, DestroyRef, OnDestroy, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { DataMap } from '@grapecity/wijmo.grid'

import {
    BaseComponent,
    DisciplinaryAction,
    EmployeeMatchResult,
    DisciplinaryActionLookups,
    FeatureType,
    Grid,
    ModalSize,
} from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { DisciplinaryActionModalComponent } from './disciplinary-action-modal.component'

@Component({
    selector: 'app-disciplinary-action-list',
    templateUrl: './disciplinary-action-list.component.html',
    styleUrls: ['./disciplinary-action-list.component.css'],
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
    ],
})
export class DisciplinaryActionListComponent extends BaseComponent implements OnDestroy {
    private destroyRef = inject(DestroyRef)
    grid: Grid<DisciplinaryAction>

    employees: EmployeeMatchResult[] = []
    employeeJobTitleMap = new Map<number, string>()
    lookups: DisciplinaryActionLookups = { hrcmCodes: [] }
    codeMap!: DataMap

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.HrDisciplinaryActionEdit])
    }

    constructor() {
        super()
        this.grid = new Grid<DisciplinaryAction>(this.services)
            .setupModalEditing(
                this.services.data.disciplinaryActions,
                'Disciplinary Action',
                DisciplinaryActionModalComponent,
                () => new DisciplinaryAction(),
                { component: this },
                ModalSize.large,
            )
            .wrapColumnHeaders()
            .isReadOnly(false)
            .filterBy(item => [
                item.employeeName,
                item.incidentDesc,
                item.discAction,
                item.code,
                item.status,
            ])

        this.grid.modified.on(this.refreshItems.bind(this))
        this.refresh()
    }

    ngOnDestroy() {
        this.grid.modified.off(this.refreshItems)
    }

    refreshItems() {
        const sub = this.services.data
            .load(true, true, this.services.data.disciplinaryActions.queryAll())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    this.initialLoadingComplete()
                    this.grid.items = result[0]
                    this.enrichItemsWithJobTitle()
                },
            })
        this.cancelableRequest('', true, sub)
    }

    buildCodeMap() {
        const items = this.lookups.hrcmCodes.map(c => ({ key: c.code, display: `${c.code} - ${c.description}` }))
        this.codeMap = new DataMap(items, 'key', 'display')
    }

    private enrichItemsWithJobTitle() {
        this.employeeJobTitleMap = new Map(this.employees.map(e => [e.hrRef, e.jobTitle]))
        this.grid.items.forEach(item => (item as any)._jobTitle = this.employeeJobTitleMap.get(item.hrRef) ?? '')
    }

    refresh() {
        const sub = this.services.data
            .load(
                true,
                true,
                this.services.data.disciplinaryActions.queryAll(),
                this.services.data.get<EmployeeMatchResult[]>('api/DisciplinaryAction/GetActiveEmployees'),
                this.services.data.get<DisciplinaryActionLookups>('api/DisciplinaryAction/GetLookups'),
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    this.grid.items = result[0]
                    this.employees = (result[1] as EmployeeMatchResult[]).sort((a, b) =>
                        `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`),
                    )
                    this.lookups = result[2] as DisciplinaryActionLookups
                    this.buildCodeMap()
                    this.enrichItemsWithJobTitle()
                    this.initialLoadingComplete()
                },
            })
        this.cancelableRequest('', true, sub)
    }
}
